/** @jsx jsx */
import React from 'react';
import { jsx, css } from '@compiled/react';
import { Box, xcss, Inline } from '@atlaskit/primitives';
import Button from '@atlaskit/button/new';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import ChevronDownIcon from '@atlaskit/icon/utility/chevron-down';
import { token } from '@atlaskit/tokens';
import { connect } from '@atlassian/jira-issue-view-react-redux/src/index.tsx';
import type { State } from '@atlassian/jira-issue-view-common-types/src/issue-type.tsx';
import { ActionButton } from '@atlassian/jira-issue-view-common-types/src/comment-type.tsx';

import { loadingMoreChildCommentsSelector } from '@atlassian/jira-issue-view-store/src/selectors/comment-selector.tsx';
import { useIntl } from '@atlassian/jira-intl';
import ReplyCommentWrapper from '../comment-reply-wrapper/index.tsx';
import { messages } from './messages.tsx';

type commentId = string;

type ReplyButtonStateProp = {
	activeLoadingButton: keyof typeof ActionButton | null;
};

type ComponentProps = {
	type: keyof typeof ActionButton;
	commentId: commentId;
	onClickHandler: (analyticsEvent: UIAnalyticsEvent) => void;
	forceShowLoadingState?: boolean;
	isReplyingToComment?: boolean;
};

type ShowMoreButtonProps = ComponentProps & ReplyButtonStateProp;

const ShowMoreContainer = ({
	onClickHandler,
	activeLoadingButton,
	type,
	forceShowLoadingState,
	isReplyingToComment,
}: ShowMoreButtonProps) => {
	const { formatMessage } = useIntl();

	return (
		<ReplyCommentWrapper
			showNextButton={type === ActionButton.NEXT}
			isLast={!isReplyingToComment && type === ActionButton.NEXT}
		>
			<Inline
				alignBlock="center"
				xcss={containerStyles}
				testId="issue-view-activity-comment.comment-show-more-replies.container"
			>
				<div
					data-testId="issue-view-activity-comment.comment-show-more-replies.empty-container"
					css={[emptyContainerStyles]}
				/>
				<Box xcss={buttonContainer}>
					<Button
						onClick={(
							e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
							analyticsEvent: UIAnalyticsEvent,
						) => onClickHandler(analyticsEvent)}
						appearance="subtle"
						iconAfter={ChevronDownIcon}
						isLoading={activeLoadingButton === type || forceShowLoadingState}
						testId="issue-view-activity-comment.comment-show-more-replies.show-more-button"
					>
						{formatMessage(messages.showMoreRepliesText)}
					</Button>
				</Box>
			</Inline>
		</ReplyCommentWrapper>
	);
};

const containerStyles = xcss({
	position: 'relative',
});

const buttonContainer = xcss({
	position: 'absolute',
});

const emptyContainerStyles = css({
	paddingTop: token('space.250'),
	paddingRight: token('space.250'),
	paddingBottom: token('space.250'),
	paddingLeft: token('space.250'),
	// 32 px for the padding given to the parent container (24+8)
	maxWidth: 'calc(100% - 32px)',
	position: 'relative',
	'--commentLineWidth': '1px',
	'--commentLineColor': token('color.background.accent.gray.subtler'),
	// 8px padding + 0.5px (to center align line of 1px) + 18px center align avatar (36px size) = 26.5px

	'--childTailWidth': token('space.200'),
	'&::after': {
		boxSizing: 'border-box',
		content: '',
		position: 'absolute',
		insetInlineStart: 'var(--commentInlineStart)',
		top: 'calc(-50% - var(--commentLineWidth)/2)',
		width: 'var(--childTailWidth)',
		borderInlineStart: 'var(--commentLineWidth) solid var(--commentLineColor)',
		borderBottom: 'var(--commentLineWidth) solid var(--commentLineColor)',
		backgroundColor: token('color.background.neutral.subtle'),
		borderEndStartRadius: token('space.100'),
		height: '100%',
	},
});

export default connect((state: State, ownProps: ComponentProps) => ({
	activeLoadingButton: loadingMoreChildCommentsSelector(ownProps.commentId)(state),
}))(ShowMoreContainer);
